import { useState } from 'react';
import { Formik, Field, ErrorMessage } from 'formik';
import { NotebookPen, X } from 'lucide-react';
import toast from 'react-hot-toast';
import CheckboxCard from 'components/Profile/CheckboxCard';
import StepShell from 'components/Profile/StepShell';
import { isMedicalHistoryComplete } from 'pages/Profile/helper';
import Groups from './Groups';
import Intro from './Intro';
import { YES_NO, choiceBtnClass, getInitialValues, validationSchema } from './helper';
import { useOutletContext } from 'react-router-dom';
import { connect } from 'react-redux';
import { ProfileActions } from 'store/actions';
import { hideAllergies } from 'components/Profile/helper';

let enhancer = connect(null, { ...ProfileActions })

const Form = ({ updateMedicalHistory, showMedicalRecords, medicalHistory = null, onSaved = null, onSubmitting = null, inModal = false, formId = '', editMode = false, onCancel = null, onStart = null }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext() || {};
  const initialData = medicalHistory ?? sectionData.medical;

  const [started, setStarted] = useState(false);
  const [records, setRecords] = useState(null);
  const showIntro = !started && !isMedicalHistoryComplete(initialData);

  const start = () => {
    setStarted(true);
    onStart?.();
  };

  const loadRecords = async () => {
    try {
      const { data } = await showMedicalRecords();
      setRecords(data.attributes);
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load medical records. Please try again later.');
    }
  };

  async function handleSubmit({ allergies, conditions, medications, medical_notes, have_no_medical_conditions }) {
    onSubmitting?.(true);

    try {
      const { data } = await updateMedicalHistory({
        patient: {
          have_no_medical_conditions,
          ...(!hideAllergies() && { allergy_ids: allergies }),
          condition_ids: conditions,
          medication_ids: medications,
          medical_notes,
        },
      });

      toast.success('Medical history saved successfully');

      if (onSaved)
        onSaved(data);
      else {
        mergeSection('medical', data);
        goToNextStep('medical');
      }

    } catch (error: any) {
      toast.error(error.errors || 'Failed to save medical history. Please try again later.');
    } finally {
      onSubmitting?.(false);
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <StepShell stepId="medical" isSubmitting={isSubmitting} bodyClassName="space-y-8" inModal={inModal} formId={formId} editMode={editMode} onCancel={onCancel} hideFooter={showIntro}>
          {showIntro ? (
            <Intro onAdd={start} onCancel={onCancel} />
          ) : (
            <>
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 rounded-xl p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-md font-bold mb-2">{hideAllergies() ? 'Any medical conditions?' : 'Any allergies or medical conditions?'}</p>
                    <p className="text-gray-500 mt-1 text-xs">Choose No if none apply — we'll skip the rest.</p>
                  </div>

                  <div className="flex w-full items-center gap-2 shrink-0 sm:w-auto">
                    {YES_NO.map(({ label, value }) => {
                      const isSelected = values.have_no_medical_conditions === !value;

                      return (
                        <button
                          key={label}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => setFieldValue('have_no_medical_conditions', !value)}
                          className={choiceBtnClass(isSelected)}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <ErrorMessage name="have_no_medical_conditions" render={(msg) => (
                  <p className="text-error text-xs mt-1 ml-1">{msg}</p>
                )} />
              </div>

              {!values.have_no_medical_conditions && (
                <>
                  <Groups records={records} onLoad={loadRecords} selected={initialData} />

                  <div className="rounded-xl border border-gray-200 bg-white">
                    <div className="flex items-center gap-3 p-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-400">
                        <NotebookPen size={18} />
                      </div>

                      <p className="min-w-0 flex-1 text-sm font-medium text-gray-500">
                        If you have any medication changes or additions, as well as surgeries or medical conditions that have changed in the last 6 months, list them here.
                      </p>
                    </div>

                    <Field name="medical_notes">
                      {({ field }) => (
                        <div className="mx-3.5 flex gap-2 border-t border-base-200 py-3.5">
                          <input
                            {...field}
                            placeholder="Describe any other conditions, medications, or concerns..."
                            className="input input-bordered w-full"
                          />
                          {values.medical_notes && (
                            <button
                              type="button"
                              className="btn btn-square btn-ghost border-gray-300 text-black bg-white hover:bg-black hover:text-white"
                              onClick={() => setFieldValue('medical_notes', '')}
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </>
              )}

              <CheckboxCard
                name="reviewed"
                label="I've reviewed this and it's accurate & up to date"
                className="bg-gray-50 border-gray-200"
              />
            </>
          )}
        </StepShell>
      )}
    </Formik>
  );
};

export default enhancer(Form);
